import { Suspense } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../shared/auth/useAuth.js';
import { Aviso } from '../shared/ui/Aviso.jsx';
import { Boton } from '../shared/ui/Boton.jsx';
import { Cargador } from '../shared/ui/Cargador.jsx';
import { TemaToggle } from '../shared/ui/Tema.jsx';

/**
 * Páginas que ya tienen sentido con la API actual.
 *
 * Las que aún no tienen endpoint NO se inventan en el menú: aparecerían como
 * botones que no hacen nada, que es peor que no aparecer.
 */
const ENLACES = [
  { a: '/panel', texto: 'Panel' },
{ a: '/lecciones', texto: 'Lecciones' },
  { a: '/proyectos', texto: 'Proyectos' },
  { a: '/recursos', texto: 'Recursos' },
  { a: '/cuenta', texto: 'Cuenta' },
];

/** Marco con cabecera, navegación y aviso de seguridad. */
export function Layout() {
  const { usuario, logout, aviso, dismissAviso, isGuest } = useAuth();
  const navigate = useNavigate();

  async function cerrarSesion() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-20 border-b border-ink-200 bg-superficie/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
          <NavLink
            to="/panel"
            className="inline-flex items-center gap-2.5 font-bold text-brand-600 dark:text-brand-800"
          >
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              className="size-8 rounded-xl ring-1 ring-ink-200"
            />
            JS Full Stack Academy
          </NavLink>

          <nav aria-label="Principal" className="flex flex-wrap items-center gap-1">
            {ENLACES.map(({ a, texto }) => (
              <NavLink
                key={a}
                to={a}
                // `NavLink` marca el enlace activo con `aria-current` y una clase.
                // Se estiliza por el estado y no comparando rutas a mano, que
                // falla en cuanto una ruta es un prefijo de otra.
                className={({ isActive }) =>
                  `rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-brand-600/10 font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-800'
                      : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                  }`
                }
              >
                {texto}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {isGuest ? (
              <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-200 dark:text-brand-800">
                Modo Demo
              </span>
            ) : null}
            <span className="hidden text-sm text-ink-500 sm:inline">{usuario?.name}</span>
            <TemaToggle />
            <Boton variante="secundario" onClick={cerrarSesion}>
              {isGuest ? 'Salir de Demo' : 'Cerrar sesión'}
            </Boton>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        {aviso ? (
          <div className="mb-6">
            <Aviso
              tipo="error"
              titulo="Se han cerrado tus sesiones"
              accion={
                <Boton variante="secundario" onClick={dismissAviso}>
                  Entendido
                </Boton>
              }
            >
              {aviso}
            </Aviso>
          </div>
        ) : null}

        {/* Al cambiar entre páginas privadas, el marco se mantiene y solo esta
            parte muestra el indicador de carga. */}
        <Suspense fallback={<Cargador texto="Cargando…" />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t border-ink-200 bg-superficie">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 py-6 text-center text-sm text-ink-500 sm:flex-row">
          <span className="inline-flex items-center gap-2">
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              className="size-5 rounded-md ring-1 ring-ink-200"
            />
            JS Full Stack Academy
          </span>
          <span>Cada proyecto sale del temario, no de una carpeta aparte.</span>
        </div>
      </footer>
    </div>
  );
}
